import useAuth, { AuthProvider } from 'Modules/firebase/use-auth';
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';

import { Button, CircularProgress, styled } from '@mui/material';
import Typography from '@mui/material/Typography';

import BasicForm, { UserProps } from '@/operator/utils/forms/BasicForm';
import { useChangeUserPassword } from '@/operator/services/hooks/users/useChangeUserPassword';
import { useUpdateUser } from '@/operator/services/hooks/users/useUpdateUser';
import { file2Base64 } from '@/operator/utils/imgFileConverto';
import useNotification from 'app/contexts/NotificationContext/hooks/useNotification';

import { useFirestoreUserQuery } from 'generated/graphql';
import { useOperator } from 'app/contexts/OperatorContext';
import { useSubdomain } from 'hooks/useSubdomain';
import { getUpdateUserInput } from 'Utils/Profile/getUpdateUserInput';
import { isEmpty } from 'lodash';

const backgroundColor = '#f7f6ff';

const StyledContainer = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '58px',
    height: '100vh',
});

const StyledImgContainer = styled('div')({
    width: '526px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    height: '100%',
});

const StyledPasswordManagerContainer = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
    padding: '24px',
    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
});

export const StyledPurpleButton = styled(Button)(({ theme }) => ({
    backgroundColor: '#4c17ad',
    '&:disabled': {
        backgroundColor: '#bf9dff',
    },
    '&:hover': {
        backgroundColor: '#4c17ad',
    },
    minWidth: '8px',
    minHeight: '25px',
    color: '#ffffff',
    textTransform: 'none',
    [theme.breakpoints.down(850)]: {
        width: '100%',
        justifyContent: 'center',
    },
}));

export const StyledWhiteButton = styled(Button)(() => ({
    backgroundColor: '#ffffff',
    color: '#5b5b5b',
    border: '1px solid #d6d6d6',
    borderRadius: '3px',
    '&:hover': {
        backgroundColor: '#ffffff',
        color: '#5b5b5b',
    },
    textAlign: 'center',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '22px',
    textTransform: 'none',
    minHeight: '40px',
    minWidth: '100px',
}));

const MAX_PROFILE_PICTURE_FILE_SIZE_IN_BYTES = Number(
    process.env.OPERATOR_MAX_PROFILE_PICTURE_FILE_SIZE_IN_BYTES,
);
const MAX_PROFILE_PICTURE_FILE_SIZE_IN_MB =
    MAX_PROFILE_PICTURE_FILE_SIZE_IN_BYTES / 1024;

function Profile() {
    const [file, setFile] = useState<File[]>([]);
    const [fileBased64, setFileBased64] = useState<string>();
    const [fileUrl, setFileUrl] = useState('');
    const [fileUploadError, setFileUploadError] = useState('');
    const { user: authUser, loading: loadingAuth, isAuthenticated } = useAuth();
    const { organizationId } = useOperator();
    const { subdomain } = useSubdomain();

    const {
        data: { firestoreUser: userFetched },
        loading,
        refetch,
    } = useFirestoreUserQuery({
        skip: loadingAuth || !isAuthenticated,
        variables: {
            where: {
                uid: authUser.uid,
                organizationId: organizationId,
            },
        },
    });
    const [enableButton, setEnableButton] = useState(false);

    const { showNotification } = useNotification();

    const userPicture = useMemo(() => userFetched?.photoURL, [userFetched]);

    const UserProps = {
        firstName: userFetched?.firstName ?? '',
        lastName: userFetched?.lastName ?? '',
        displayName: userFetched?.displayName,
        pronouns: userFetched?.pronouns,
        avatarUrl: userPicture,
        email: userFetched?.email ?? '',
    };

    const [userProfileState, setUserProfileState] =
        useState<UserProps>(UserProps);

    const { updateUser, updateUserLoading, updateUserData } = useUpdateUser();
    const { changeUserPassword, changePasswordLoading } =
        useChangeUserPassword();

    const isUserLoggedWithEmailPassword =
        authUser.provider === AuthProvider.emailAndPassword;

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!event.target.files || !file) {
            return;
        }

        if (file.size / 1000 > MAX_PROFILE_PICTURE_FILE_SIZE_IN_BYTES) {
            setFileUploadError(
                `File size is greater than ${MAX_PROFILE_PICTURE_FILE_SIZE_IN_MB}mb.`,
            );
            return;
        }

        setFileUploadError('');
        setFile([file]);
        setFileUrl(URL.createObjectURL(file));
    };

    const convertFileToBase64 = async (file: File): Promise<void> => {
        const dataUrl = await file2Base64(file);

        const base64Content = dataUrl.split(',')[1];

        const sanitizedBase64Content = base64Content
            .split(' fileBased64')[0]
            .replace('fileBased64', '');

        setFileBased64(sanitizedBase64Content);
    };

    useLayoutEffect(() => {
        document.body.style.backgroundColor = backgroundColor;

        return () => {
            document.body.style.backgroundColor = '#FFFFFF';
        };
    }, []);

    const undo = useCallback(() => {
        if (userFetched?.photoURL) {
            setUserProfileState((userProfileState) => ({
                ...userProfileState,
                avatarUrl: userPicture,
            }));
        }
        if (userFetched?.firstName) {
            setUserProfileState((userProfileState) => ({
                ...userProfileState,
                firstName: userFetched.firstName || '',
                pronouns: userFetched?.pronouns
                    ? userFetched.pronouns
                    : undefined,
                displayName: userFetched?.displayName
                    ? userFetched.displayName
                    : undefined,
            }));
        }
        if (userFetched?.lastName) {
            setUserProfileState((userProfileState) => ({
                ...userProfileState,
                lastName: userFetched.lastName || '',
            }));
        }

        setFile([]);
        setFileUrl('');
    }, [userFetched]);

    useEffect(() => {
        undo();
        setUserProfileState(UserProps);
    }, [userFetched]);

    useEffect(() => {
        if (file[0]) {
            convertFileToBase64(file[0]);
        } else {
            setFileBased64(undefined);
        }
        setUserProfileState(UserProps);
    }, [file]);

    const handleUserPasswordChange = () => {
        const email = authUser?.email;
        if (!email) {
            showNotification({
                message: 'Could not change password, please try again later',
                severity: 'error',
            });

            return;
        }

        return changeUserPassword(email, subdomain);
    };

    useEffect(() => {
        if (updateUserData) {
            refetch();
        }
    }, [updateUserData]);

    const handleEnableButton = () => {
        const newUpdateUserInput = getUpdateUserInput(
            userFetched,
            userProfileState,
        );

        const requiredFieldsAreNotEmpty =
            userProfileState.firstName && userProfileState.lastName;

        if (!isEmpty(newUpdateUserInput) && requiredFieldsAreNotEmpty) {
            setEnableButton(true);
        } else setEnableButton(false);
    };

    const handleOnSave = async () => {
        const newUpdateUserInput = getUpdateUserInput(
            userFetched,
            userProfileState,
        );
        if (!isEmpty(newUpdateUserInput)) {
            await updateUser(userFetched?.id, newUpdateUserInput);
        }
    };

    useEffect(() => {
        if (userProfileState) {
            handleEnableButton();
        }
    }, [userProfileState]);

    return (
        <StyledContainer>
            {loading ? (
                <CircularProgress style={{ color: '#4C17AD' }} />
            ) : (
                <StyledImgContainer>
                    <BasicForm
                        showLabel
                        editProfilePicture={isUserLoggedWithEmailPassword}
                        onFormStateChanged={setUserProfileState}
                        handleFileChange={handleFileChange}
                        currentState={userProfileState}
                        fileUrl={fileUrl}
                        fileUploadError={fileUploadError}
                    />

                    {isUserLoggedWithEmailPassword && (
                        <StyledPasswordManagerContainer>
                            <div>
                                <Typography variant="h5" style={{ margin: 0 }}>
                                    Password Management
                                </Typography>
                                <Typography
                                    variant="body2"
                                    style={{
                                        margin: 0,
                                        color: '#5B5B5B',
                                    }}
                                >
                                    Update or reset your Quality password.
                                </Typography>
                            </div>
                            <div>
                                {!changePasswordLoading ? (
                                    <StyledWhiteButton
                                        size="small"
                                        onClick={handleUserPasswordChange}
                                    >
                                        Change Password
                                    </StyledWhiteButton>
                                ) : (
                                    <CircularProgress />
                                )}
                            </div>
                        </StyledPasswordManagerContainer>
                    )}
                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '24px',
                            alignItems: 'center',
                        }}
                    >
                        {!updateUserLoading ? (
                            <>
                                <StyledPurpleButton
                                    size="small"
                                    type="submit"
                                    variant="contained"
                                    style={{
                                        width: '100%',
                                        justifyContent: 'center',
                                    }}
                                    onClick={() => handleOnSave()}
                                    disabled={!enableButton}
                                >
                                    Save Changes
                                </StyledPurpleButton>
                                <span onClick={undo} onKeyDown={undo}>
                                    <Typography
                                        variant="h5"
                                        margin={0}
                                        style={{
                                            color: '#999999',
                                            justifyContent: 'center',
                                            cursor: !enableButton
                                                ? 'not-allowed'
                                                : 'pointer',
                                        }}
                                    >
                                        Undo
                                    </Typography>
                                </span>
                            </>
                        ) : (
                            <CircularProgress />
                        )}
                    </div>
                </StyledImgContainer>
            )}
        </StyledContainer>
    );
}

export default Profile;
